import { usePitcherApi } from '@sdk/main'
import { unref, computed } from 'vue'
import type { InfiniteData } from '@tanstack/vue-query'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import type { CanvasRetrieve } from '@openapi'
import { getNextPageParam, getDefinedProps, DEFAULT_ITEMS_PER_PAGE } from '@lib/util/query'
import type { GetCanvasesParams } from '@lib/types/canvases'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { MaybeRef } from '@vueuse/core'

export const CANVASES = 'canvases'

type Options = {
  apiFn?: (params: GetCanvasesParams) => Promise<PaginatedData<CanvasRetrieve>>
}

export const useCanvasesAsInfinity = (params: MaybeRef<GetCanvasesParams>, options: Options = {}) =>
  useInfiniteQuery(
    [CANVASES, params],
    ({ pageParam }): Promise<PaginatedData<CanvasRetrieve>> => {
      const props: GetCanvasesParams = getDefinedProps({
        ...unref(params),
        page_size: unref(params).page_size || DEFAULT_ITEMS_PER_PAGE,
        page: pageParam,
      })

      if (options.apiFn) {
        return options.apiFn(props)
      }

      return usePitcherApi().getCanvases(props)
    },
    {
      getNextPageParam,
      initialData: (): InfiniteData<PaginatedData<CanvasRetrieve>> => ({
        pages: [],
        pageParams: [],
      }),
      enabled: computed(() => !!unref(params).instance_id),
    },
  )

export const useFetchCanvases = (params: MaybeRef<GetCanvasesParams>, options?: { enabled?: boolean }) =>
  useQuery([CANVASES, params], () => usePitcherApi().getCanvases(getDefinedProps(unref(params))), options)

export const useCanvasById = (id: MaybeRef<CanvasRetrieve['id']>) => {
  return useQuery(['canvas-by-id', id], () => usePitcherApi().getCanvas({ id: unref(id) }), {
    enabled: computed(() => !!unref(id)),
  })
}
